import React, { ComponentType, FC, ReactNode, createContext, useCallback, useContext, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import * as Router from 'react-router';
import * as RouterDom from 'react-router-dom';
import { Action, Router as RemixRouter } from '@remix-run/router';
import RouterContext from 'ia-react-core/~/components/navigation/_RouterContext';
import { LegacyHistory } from 'ia-react-core/~/interfaces/LegacyHistory';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';

// Alias that allows checking unknown types
const RouterAlias = Router as any;
const RouterDomAlias = RouterDom as any;

export default {};

export { useLocation } from 'react-router-dom';

export const Switch = RouterAlias.Routes;

const RouterContentContext = createContext<ReactNode>(null);

export const getRoute = ({ path, exact, component, render, ...props }: Router.RouteProps & {
  key?: string;
  exact?: boolean;
  render?: () => ReactNode;
  component?: ComponentType;
}) => (
  <RouterAlias.Route
    element={render?.()}
    Component={component}
    {...props}
    path={!exact ? `${path}/*` : path}
  />
);

export const RouterWrapper = ({ router, children }: { router: RemixRouter; children?: ReactNode }) => {
  const blockCallbacksRef = useRef<((location: Router.Location) => boolean | void)[]>([]);
  const content = useContext(RouterContentContext);

  RouterAlias.useBlocker(({ nextLocation }: { nextLocation: Router.Location }) => {
    if (blockCallbacksRef.current.length) {
      const shouldBlock = blockCallbacksRef.current.some((callback) => (
        callback(nextLocation) === false
      ));

      return shouldBlock;
    }

    return false;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verifyIfShouldProceed = useCallback(debounce(() => {
    const blockers = Array.from(router.state.blockers.values());
    const blockedBlockers = blockers.filter(({ state }) => state === 'blocked');

    if (blockCallbacksRef.current.length) {
      blockedBlockers?.forEach((blocker) => blocker.reset());
    } else {
      blockedBlockers?.forEach((blocker) => blocker.proceed());
    }
  }, 50), [router]);

  const addBlockerCallback = useCallback((callback: (location: Router.Location) => void) => {
    blockCallbacksRef.current.push(callback);
  }, []);

  const removeBlockerCallback = useCallback((callback: (location: Router.Location) => void) => {
    const index = blockCallbacksRef.current.indexOf(callback);
    blockCallbacksRef.current.splice(index, 1);
    verifyIfShouldProceed();
  }, [verifyIfShouldProceed]);

  const routerContext = useMemo(() => ({
    router,
    addBlockerCallback,
    removeBlockerCallback,
  }), [addBlockerCallback, removeBlockerCallback, router]);

  return (
    <RouterContext.Provider value={routerContext}>{children || content}</RouterContext.Provider>
  );
};

export const BrowserRouter = ({ children, basename, ...props }: { children: ReactNode; basename?: string }) => {
  let router: RemixRouter;
  const RouterComponent = useCallback(() => <RouterWrapper router={router} />, [router]);
  router = useMemo(() => (
    RouterDomAlias.createBrowserRouter([{ path: '*', Component: RouterComponent }], { basename, ...props })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);
  return (
    <RouterContentContext.Provider value={children}>
      <RouterAlias.RouterProvider router={router} />
    </RouterContentContext.Provider>
  );
};

export const HashRouter = ({ children, ...props }: { children: ReactNode }) => {
  let router: RemixRouter;
  const RouterComponent = useCallback(() => <RouterWrapper router={router}>{children}</RouterWrapper>, [children, router]);
  router = useMemo(() => (
    RouterDomAlias.createHashRouter([{ path: '*', Component: RouterComponent }], props)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);
  return <RouterAlias.RouterProvider router={router} />;
};

export const MemoryRouter = ({ children, ...props }: { children: ReactNode }) => {
  let router: RemixRouter;
  const RouterComponent = useCallback(() => <RouterWrapper router={router} />, [router]);
  router = useMemo(() => (
    RouterDomAlias.createMemoryRouter([{ path: '*', Component: RouterComponent }], props)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);
  return (
    <RouterContentContext.Provider value={children}>
      <RouterAlias.RouterProvider router={router} />
    </RouterContentContext.Provider>
  );
};

export const useHistory = () => {
  const navigate = RouterAlias.useNavigate();
  const { router, addBlockerCallback, removeBlockerCallback } = useContext(RouterContext);

  const normalizeLocation = useCallback((loc: Router.Location) => {
    const parts = loc.pathname.split(router.basename).slice(1);
    return {
      ...loc,
      pathname: `/${parts.join('/')}`,
    };
  }, [router.basename]);

  const listen = useCallback((callback: (loc: Router.Location, action: Action) => void) => (
    router.subscribe(({ location, historyAction }) => {
      callback(location, historyAction);
    })
  ), [router]);

  const replace = useCallback(
    (pathname: Router.To, state?: any) => (
      navigate(pathname, { replace: true, state, relative: 'path' })
    ),
    [navigate],
  );

  const block = useCallback((callback: (newLocation: Router.Location) => boolean) => {
    addBlockerCallback(callback);
    return () => {
      removeBlockerCallback(callback);
    };
  }, [addBlockerCallback, removeBlockerCallback]);

  const history: LegacyHistory = useMemo(() => ({
    push: (to: Router.To, options: Router.NavigateOptions = {}) => navigate(to, { relative: 'path', ...options }),
    go: navigate,
    replace,
    block,
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    listen,
    get location() { return normalizeLocation(router.state.location); },
    get action() { return router.state.historyAction; },
  }), [block, listen, navigate, normalizeLocation, replace, router]);

  return history;
};

export const withRouter = <Props, >(WrappedComponent: FC<Props>) => {
  const WithRouter: FC<Props & { history: LegacyHistory; location: Router.Location }> = (props: Props) => {
    const history = useHistory();
    const location = Router.useLocation();

    return (
      <WrappedComponent
        history={history}
        location={location}
        {...props}
      />
    );
  };
  WithRouter.displayName = getWrappedDisplayName('WithRouter', WrappedComponent);

  return WithRouter;
};

// Exact does not exist anymore, but we still need it. "end"" can be used instead.
export const matchPath = (pathname: string, route: { path: string; exact?: boolean }) => {
  if (route.path === null) return false;

  const match = Router.matchPath({
    path: route.path,
    end: Boolean(route.exact),
  }, pathname);

  return Boolean(match);
};
